import React from 'react';
import { Link } from 'react-router-dom';
import {
  Receipt,
  Calculator,
  FileText,
  CheckCircle,
  ArrowRight,
  Shield,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Users,
  ClipboardCheck,
  FileSearch,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema } from '../utils/seoSchemas';

import RelatedServices from '../components/RelatedServices';
const VATServicesUAE = () => {
  usePageSeo({
    title: 'VAT Services in UAE | Registration, Filing & Compliance',
    description:
      'Professional UAE VAT services — registration, return filing, reconciliation, health check & FTA audit support. Stay compliant with WPAA.',
    path: '/vat-services-uae',
    image: '/og/vat-services-uae.png'
  });

  const services = [
    {
      title: 'VAT Registration & Deregistration',
      description: 'Seamless VAT registration and deregistration with the Federal Tax Authority (FTA).',
      icon: FileText
    },
    {
      title: 'VAT Return Filing (FTA compliant)',
      description: 'Accurate, timely preparation and submission of periodic VAT returns.',
      icon: Calculator
    },
    {
      title: 'VAT Advisory & Planning',
      description: 'Strategic VAT advisory for complex transactions, contracts and business structures.',
      icon: TrendingUp
    },
    {
      title: 'Input/Output VAT Reconciliation',
      description: 'Reconciliation of input and output VAT to ensure accurate reporting and recoverability.',
      icon: RefreshCw
    },
    {
      title: 'VAT Health Check & Review',
      description: 'Comprehensive VAT review to identify compliance gaps, risks and optimisation opportunities.',
      icon: ClipboardCheck
    },
    {
      title: 'VAT Audit Support',
      description: 'Full representation and documentation support during FTA audits and assessments.',
      icon: FileSearch
    }
  ];

  const whoNeedsVAT = [
    { text: 'Businesses exceeding AED 375,000 turnover', icon: TrendingUp },
    { text: 'Companies requiring VAT return filing', icon: FileText },
    { text: 'Businesses with incorrect VAT records', icon: AlertTriangle },
    { text: 'Companies facing VAT audits or notices', icon: Shield }
  ];

  const whyChooseUs = [
    'FTA-compliant VAT experts',
    'Accurate and timely filing',
    'Strong reconciliation and reporting',
    'Practical advisory support'
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-vat-service"
        data={serviceSchema({
          name: 'VAT Services in UAE',
          description:
            'UAE VAT registration, deregistration, return filing, reconciliation, health checks and FTA audit support.',
          path: '/vat-services-uae',
          serviceType: 'VAT Services'
        })}
      />
      <JsonLd
        id="ld-vat-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'VAT Services UAE', path: '/vat-services-uae' }
        ])}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Receipt className="text-blue-400" size={32} />
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">VAT Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              VAT Services in UAE
            </h1>
            <p className="text-2xl text-blue-200 mb-6 font-semibold">
              Registration, Filing & Compliance
            </p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-3xl">
              VAT compliance in the UAE requires accurate reporting, timely filing, and proper documentation.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              WPAA helps businesses stay fully compliant with UAE VAT regulations and avoid costly penalties.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=vat">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg" data-testid="vat-consultation-btn">
                  Get VAT Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+971501219341" data-testid="vat-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our VAT Services */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our VAT Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end VAT solutions for businesses of all sizes operating in the UAE.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-blue-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-blue-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Needs VAT Services */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Needs VAT Services?</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whoNeedsVAT.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-blue-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-8">Why Choose WPAA?</h2>
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-blue-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <Users className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Trusted VAT Compliance Partner</h3>
              <p className="text-blue-100 mb-6">
                With over 17 years of experience, our team ensures your business stays VAT-compliant with UAE regulations while keeping reporting clean and accurate.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-3xl font-bold">18+</p>
                  <p className="text-sm text-blue-200">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm text-blue-200">Clients Served</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Avoid VAT Penalties Warning */}
      <section className="py-16 px-4 bg-amber-50 border-y-4 border-amber-400">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="bg-amber-500 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="text-white" size={32} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-amber-900 mb-2">Avoid VAT Penalties</h3>
              <p className="text-lg text-amber-800">
                Incorrect VAT filing or delays can result in heavy fines. Proper compliance ensures smooth business operations.
              </p>
            </div>
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'Corporate Tax Services', description: '9% UAE Corporate Tax — registration, filing, and transfer pricing support.', path: '/corporate-tax-uae', accent: 'teal' },
          { title: 'Accounting Services', description: 'VAT-compliant bookkeeping on Zoho Books or QuickBooks.', path: '/accounting-services-uae', accent: 'indigo' },
          { title: 'E-Invoicing Services', description: 'Readiness, invoice data review, ASP selection and integration coordination.', path: '/e-invoicing-uae', accent: 'green' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Need VAT Assistance?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Whether you need registration, filing, reconciliation or audit support, our VAT experts are here to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?service=vat">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="vat-cta-consultation-btn">
                Get VAT Help
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="vat-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VATServicesUAE;
